import React, { useState, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { Checkbox } from '@packages/ui/primitives/Checkbox';
import { Button } from '@packages/ui/primitives/Button';

import type { InternalColumnDef, InternalSubRowConfig, InternalActionDef } from '../schema/types';
import type { PluginRegistry } from '../plugins/PluginRegistry';
import { CellRenderer } from './CellRenderer';
import { ActionRenderer } from './ActionRenderer';
import type { SubRowContext } from './ActionRenderer';
import { getPinnedCellStyle, getPinnedBoundaryShadow, type PinOffsets } from './computePinOffsets';
import { TABLE_IDS } from '../semantic-contract';

interface RowRendererProps {
  row: Record<string, unknown>;
  rowIndex: number;
  columns: ReadonlyArray<InternalColumnDef>;
  registry: PluginRegistry;
  namespace: string;
  showCheckbox: boolean;
  /** Whether this specific row passes the `selectable` condition. When false the checkbox cell is empty. */
  rowSelectable: boolean;
  isSelected: boolean;
  onSelect: (checked: boolean) => void;
  rowActions: ReadonlyArray<InternalActionDef>;
  onExecuteAction: (
    action: InternalActionDef,
    row: Record<string, unknown>,
    context?: SubRowContext
  ) => void;
  checkPermission?: (perm: { action: string; resource: string }) => boolean;
  subRowConfig: InternalSubRowConfig | null;
  subRowColumns: ReadonlyArray<InternalColumnDef>;
  editingRow: Record<string, unknown> | null;
  onCellDoubleClick?: (rowKey: string, field: string) => void;
  onEditChange?: (field: string, value: unknown) => void;
  onEditBlur?: (field: string) => void;
  editErrors?: Record<string, string>;
  injectedData?: Map<string, Array<{ label: string; value: string | number }>>;
  subRowInjectedData?: Map<string, Array<{ label: string; value: string | number }>>;
  keyField: string;
  striped: boolean;
  editingSubRows?: Map<string, Record<string, unknown>>;
  subRowEditErrors?: Map<string, Record<string, string>>;
  onSubRowCellDoubleClick?: (parentKey: string, subIndex: number, field: string) => void;
  onSubRowEditChange?: (parentKey: string, subIndex: number, field: string, value: unknown) => void;
  onSubRowEditBlur?: (parentKey: string, subIndex: number, field: string) => void;
  subRowActions?: ReadonlyArray<InternalActionDef>;
  expandedRows?: Set<string>;
  onToggleExpand?: (rowKey: string) => void;
  isGroupChild?: boolean;
  showActionsColumn?: boolean;
  onRowSave?: (rowKey: string) => void;
  onRowCancel?: (rowKey: string) => void;
  saveLoading?: boolean;
  /** When true, per-row save/cancel buttons are hidden (external controller manages editing). */
  externalEditControl?: boolean;
  /** When false, inline editors do not auto-focus on mount. */
  editorAutoFocus?: boolean;
  /** Total number of <col> elements in the parent table (for sub-row filler cells). */
  parentColCount: number;
  /**
   * Extra invisible columns added to accommodate sub-rows that have more data
   * columns than the parent. Parent rows render empty filler cells for these.
   */
  subRowOverflowCols: number;
  pinOffsets?: PinOffsets;
  /** Optional per-row style from schema. Takes priority over striped background. */
  rowStyleFn?: (
    row: Record<string, unknown>,
    index: number
  ) => Record<string, string | number> | undefined;
  /** Called when a row is clicked (for block-event publishing). */
  onRowClick?: (row: Record<string, unknown>, rowKey: string) => void;
}

export const RowRenderer: React.FC<RowRendererProps> = React.memo(
  ({
    row,
    rowIndex,
    columns,
    registry,
    namespace,
    showCheckbox,
    rowSelectable,
    isSelected,
    onSelect,
    rowActions,
    onExecuteAction,
    checkPermission,
    subRowConfig,
    subRowColumns,
    editingRow,
    onCellDoubleClick,
    onEditChange,
    onEditBlur,
    editErrors,
    injectedData,
    subRowInjectedData,
    keyField,
    striped: _striped,
    editingSubRows,
    subRowEditErrors,
    onSubRowCellDoubleClick,
    onSubRowEditChange,
    onSubRowEditBlur,
    subRowActions,
    expandedRows,
    onToggleExpand,
    isGroupChild = false,
    showActionsColumn = false,
    onRowSave,
    onRowCancel,
    saveLoading = false,
    externalEditControl = false,
    editorAutoFocus = false,
    parentColCount,
    subRowOverflowCols = 0,
    pinOffsets,
    rowStyleFn,
    onRowClick,
  }) => {
    const _rawKey = row[keyField];
    const rowKey = (_rawKey != null && _rawKey !== '') ? String(_rawKey) : `__idx_${rowIndex}`;
    const hasSubRows = subRowConfig && Array.isArray(row[subRowConfig.dataField]);
    const subRows = hasSubRows ? (row[subRowConfig!.dataField] as unknown[]) : [];

    const subRowExpandMode = subRowConfig?.expandMode ?? 'alwaysOpen';
    const alwaysOpen = subRowExpandMode === 'alwaysOpen';

    // Local expand state (fallback when table-level expand is not provided)
    const [localExpanded, setLocalExpanded] = useState(
      subRowExpandMode === 'expanded' || subRowExpandMode === 'alwaysOpen'
    );

    const isExpanded = alwaysOpen ? true : expandedRows ? expandedRows.has(rowKey) : localExpanded;

    const showExpandButton = hasSubRows && !alwaysOpen;

    const handleToggleExpand = () => {
      if (onToggleExpand) {
        onToggleExpand(rowKey);
      } else {
        setLocalExpanded(!localExpanded);
      }
    };

    const hasActionsColumn = rowActions.length > 0 || showActionsColumn;
    const hasRowErrors = editErrors != null && Object.keys(editErrors).length > 0;

    const hasEditingSubRows =
      editingSubRows != null &&
      Array.from(editingSubRows.keys()).some((k) => k.startsWith(`${rowKey}.`));
    const isRowOrSubRowEditing = editingRow !== null || hasEditingSubRows;

    const hasDirtyFields = useMemo(() => {
      if (editingRow === null) return false;
      const parentDirty = Object.entries(editingRow).some(([field, value]) => value !== row[field]);
      if (parentDirty) return true;
      if (!editingSubRows || !subRowConfig) return false;
      const subField = subRowConfig.dataField;
      const originalSubs = (row[subField] as unknown[] | undefined) ?? [];
      for (const [key, edited] of editingSubRows.entries()) {
        if (!key.startsWith(`${rowKey}.`)) continue;
        const subIdx = parseInt(key.split('.').pop()!, 10);
        const originalSub = (originalSubs[subIdx] ?? {}) as Record<string, unknown>;
        const subDirty = Object.entries(edited).some(([f, v]) => v !== originalSub[f]);
        if (subDirty) return true;
      }
      return false;
    }, [editingRow, row, editingSubRows, subRowConfig, rowKey]);

    const scrollToFirstEditable = useCallback(
      (clickedField: string) => {
        const clickedCol = columns.find((c) => c.field === clickedField);
        if (clickedCol?.editable !== null) return;

        const firstEditable = columns.find((c) => c.editable !== null);
        if (!firstEditable) return;

        requestAnimationFrame(() => {
          const cellEl = document.getElementById(TABLE_IDS.cell(rowIndex, firstEditable.field));
          if (!cellEl) return;

          const scrollContainer =
            cellEl.closest<HTMLElement>('[style*="overflow"]') ??
            cellEl.closest<HTMLElement>('.height-identifier');

          if (scrollContainer) {
            const containerRect = scrollContainer.getBoundingClientRect();
            const cellRect = cellEl.getBoundingClientRect();

            const targetScrollLeft =
              scrollContainer.scrollLeft +
              (cellRect.left - containerRect.left) -
              containerRect.width / 2 +
              cellRect.width / 2;

            const clampedLeft = Math.max(
              0,
              Math.min(targetScrollLeft, scrollContainer.scrollWidth - containerRect.width)
            );

            scrollContainer.scrollTo({ left: clampedLeft, behavior: 'smooth' });
          } else {
            cellEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
          }
        });
      },
      [columns, rowIndex]
    );

    const hasAnyPinned =
      pinOffsets != null && (pinOffsets.left.size > 0 || pinOffsets.right.size > 0);
    const customRowStyle = rowStyleFn?.(row, rowIndex);
    const isStriped = !customRowStyle && _striped && rowIndex % 2 !== 0;
    const stripedBg = customRowStyle?.background
      ? String(customRowStyle.background)
      : isStriped
      ? '#f8f8fe'
      : '#ffffff';
    const rowBg = customRowStyle?.background
      ? String(customRowStyle.background)
      : isStriped
      ? 'rgba(79, 70, 229, 0.04)'
      : undefined;

    return (
      <>
        <tr
          className={clsx(hasRowErrors && 'error-row')}
          data-testid={`dt-v2-row-${rowIndex}`}
          onClick={onRowClick ? () => onRowClick(row, rowKey) : undefined}
          style={
            hasAnyPinned ? { background: stripedBg } : rowBg ? { background: rowBg } : undefined
          }>
          {showCheckbox && (
            <td
              style={{
                paddingLeft: 8,
                verticalAlign: 'middle',
                ...(pinOffsets && pinOffsets.left.size > 0
                  ? { position: 'sticky' as const, left: 0, zIndex: 1, backgroundColor: stripedBg }
                  : {}),
              }}>
              {rowSelectable && (
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => onSelect(e.target.checked)}
                  semanticId={TABLE_IDS.rowCheckbox(rowIndex)}
                />
              )}
            </td>
          )}
          {isGroupChild && <td style={{ width: 45 }} />}
          {columns.map((col, colIndex) => {
            const pinStyle = pinOffsets
              ? getPinnedCellStyle(col, pinOffsets, false, stripedBg)
              : {};
            const shadow = pinOffsets
              ? getPinnedBoundaryShadow(col, columns as InternalColumnDef[])
              : null;
            const fieldError = editErrors?.[col.field];
            const isEditableCell = editingRow !== null && col.editable !== null;
            const showExpandInCell = colIndex === 0 && showExpandButton;
            const expandButton = showExpandInCell ? (
              <Button
                semanticId={TABLE_IDS.expandRow(rowIndex)}
                type='button'
                variant='ghost'
                size='sm'
                icon={isExpanded ? 'keyboard_arrow_down' : 'chevron_right'}
                onClick={handleToggleExpand}
                data-testid={`dt-v2-expand-${rowIndex}`}
              />
            ) : null;
            const cellRenderer = (
              <CellRenderer
                column={col}
                row={row}
                rowIndex={rowIndex}
                isEditing={editingRow !== null && col.editable !== null}
                registry={registry}
                injectedData={injectedData}
                onDoubleClick={
                  onCellDoubleClick ? () => onCellDoubleClick(rowKey, col.field) : undefined
                }
                editValue={editingRow?.[col.field]}
                onEditChange={(val) => onEditChange?.(col.field, val)}
                onSideEffect={(patches) => patches.forEach(p => onEditChange?.(p.field, p.value))}
                onEditBlur={() => onEditBlur?.(col.field)}
                editError={editErrors?.[col.field]}
                editorAutoFocus={editorAutoFocus}
              />
            );
            return (
              <td
                key={col.id}
                id={TABLE_IDS.cell(rowIndex, col.field)}
                className={clsx(fieldError && 'error-cell')}
                style={{
                  textAlign:
                    col.align === 'center' ? 'center' : col.align === 'right' ? 'right' : 'left',
                  verticalAlign: showExpandInCell ? 'middle' : isEditableCell ? 'top' : 'middle',
                  ...pinStyle,
                  ...(shadow ? { boxShadow: shadow } : {}),
                }}
                onDoubleClick={
                  onCellDoubleClick
                    ? () => {
                        onCellDoubleClick(rowKey, col.field);
                        scrollToFirstEditable(col.field);
                      }
                    : undefined
                }
                data-testid={`dt-v2-cell-${rowIndex}-${col.field}`}>
                {showExpandInCell ? (
                  <div
                    data-testid={`dt-v2-expand-cell-${rowIndex}-${col.field}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0,
                      minWidth: 0,
                      width: '100%',
                    }}>
                    {expandButton}
                    <div style={{ flex: 1, minWidth: 0 }}>{cellRenderer}</div>
                  </div>
                ) : (
                  cellRenderer
                )}
              </td>
            );
          })}
          {subRowOverflowCols > 0 &&
            Array.from({ length: subRowOverflowCols }, (_, i) => (
              <td key={`overflow-${i}`} className='indent-cell' />
            ))}
          {hasActionsColumn && (
            <td
              style={
                editingRow !== null
                  ? { overflow: 'visible', verticalAlign: 'top' }
                  : { overflow: 'hidden' }
              }>
              {isRowOrSubRowEditing && !externalEditControl ? (
                <span
                  style={{
                    display: 'flex',
                    gap: 4,
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                  }}>
                  <Button
                    semanticId={TABLE_IDS.rowSave(rowKey)}
                    size='sm'
                    type='button'
                    variant='rounded-icon-secondary'
                    icon={saveLoading ? undefined : 'save'}
                    loading={saveLoading}
                    disabled={saveLoading || !hasDirtyFields}
                    onClick={() => onRowSave?.(rowKey)}
                    data-testid={`dt-v2-row-save-${rowKey}`}
                  />
                  <Button
                    semanticId={TABLE_IDS.rowCancel(rowKey)}
                    size='sm'
                    type='button'
                    variant='rounded-icon-secondary-dark'
                    icon='close'
                    disabled={saveLoading}
                    onClick={() => onRowCancel?.(rowKey)}
                    data-testid={`dt-v2-row-cancel-${rowKey}`}
                  />
                </span>
              ) : rowActions.length > 0 ? (
                <ActionRenderer
                  actions={rowActions}
                  row={row}
                  rowIndex={rowIndex}
                  namespace={namespace}
                  onExecuteAction={onExecuteAction}
                  checkPermission={checkPermission}
                />
              ) : null}
            </td>
          )}
        </tr>

        {isExpanded && subRows.length > 0 && (
          <SubRowBlock
            rowKey={rowKey}
            rowIndex={rowIndex}
            subRows={subRows}
            subRowColumns={subRowColumns}
            parentColCount={parentColCount + subRowOverflowCols}
            showCheckbox={showCheckbox}
            hasActionsColumn={hasActionsColumn}
            indentEnabled={subRowConfig?.indent ?? true}
            registry={registry}
            namespace={namespace}
            subRowInjectedData={subRowInjectedData}
            editingSubRows={editingSubRows}
            subRowEditErrors={subRowEditErrors}
            onSubRowCellDoubleClick={onSubRowCellDoubleClick}
            onSubRowEditChange={onSubRowEditChange}
            onSubRowEditBlur={onSubRowEditBlur}
            subRowActions={subRowActions}
            rowActions={rowActions}
            onExecuteAction={onExecuteAction}
            checkPermission={checkPermission}
            row={row}
            editorAutoFocus={editorAutoFocus}
          />
        )}
      </>
    );
  }
);

RowRenderer.displayName = 'RowRenderer';

// ---------------------------------------------------------------------------
// SubRowBlock — renders sub-row header + data rows inside the parent table.
//
// Sub-row columns have their own widths and are NOT mapped 1:1 to parent
// column positions. Layout per <tr>:
//
//   [indent] [sub-col 1 … N (with own widths)] [filler …to match total] [action]
//
// • indent   — single empty cell for visual nesting
// • sub-cols — ALL sub-row columns are rendered (never truncated)
// • fillers  — empty cells to pad so total <td> count equals parentColCount
//
// When sub-rows define more data columns than the parent, the parent
// (RowRenderer) adds overflow filler cells and inflates parentColCount
// so both parent and sub-row <tr> elements have the same total cell count.
// ---------------------------------------------------------------------------

interface SubRowBlockProps {
  rowKey: string;
  rowIndex: number;
  subRows: unknown[];
  subRowColumns: ReadonlyArray<InternalColumnDef>;
  parentColCount: number;
  showCheckbox: boolean;
  hasActionsColumn: boolean;
  indentEnabled: boolean;
  registry: PluginRegistry;
  namespace: string;
  subRowInjectedData?: Map<string, Array<{ label: string; value: string | number }>>;
  editingSubRows?: Map<string, Record<string, unknown>>;
  subRowEditErrors?: Map<string, Record<string, string>>;
  onSubRowCellDoubleClick?: (parentKey: string, subIndex: number, field: string) => void;
  onSubRowEditChange?: (parentKey: string, subIndex: number, field: string, value: unknown) => void;
  onSubRowEditBlur?: (parentKey: string, subIndex: number, field: string) => void;
  subRowActions?: ReadonlyArray<InternalActionDef>;
  rowActions: ReadonlyArray<InternalActionDef>;
  onExecuteAction: (
    action: InternalActionDef,
    row: Record<string, unknown>,
    context?: SubRowContext
  ) => void;
  checkPermission?: (perm: { action: string; resource: string }) => boolean;
  row: Record<string, unknown>;
  editorAutoFocus?: boolean;
}

const SubRowBlock: React.FC<SubRowBlockProps> = ({
  rowKey,
  rowIndex,
  subRows,
  subRowColumns,
  parentColCount,
  showCheckbox,
  hasActionsColumn,
  indentEnabled,
  registry,
  namespace,
  subRowInjectedData,
  editingSubRows,
  subRowEditErrors,
  onSubRowCellDoubleClick,
  onSubRowEditChange,
  onSubRowEditBlur,
  subRowActions,
  rowActions,
  onExecuteAction,
  checkPermission,
  row,
  editorAutoFocus = false,
}) => {
  const hasSubRowActions = subRowActions != null && subRowActions.length > 0;

  // Every sub-row <tr> must produce exactly parentColCount cells.
  // Layout: [indent-td?] [sub-col 1..N] [filler...] [action-td?]
  //
  // indent-td occupies one of the parent's column slots.
  // action-td occupies the parent's action column slot.
  // All sub-row columns are rendered — parent rows add overflow cells when needed.
  const actionSlot = hasActionsColumn ? 1 : 0;
  const indentSlot = indentEnabled ? 1 : 0;
  const availableForSubCols = parentColCount - indentSlot - actionSlot;
  const fillerCount = Math.max(0, availableForSubCols - subRowColumns.length);

  return (
    <>
      <tr className='subrow-header'>
        {indentEnabled && <th className='indent-cell' />}
        {subRowColumns.map((subCol) => (
          <th
            key={subCol.id}
            style={{ textAlign: subCol.align, width: subCol.width.default }}
            className='subrow-header-cell'>
            {subCol.header}
          </th>
        ))}
        {Array.from({ length: fillerCount }, (_, i) => (
          <th key={`filler-h-${i}`} className='indent-cell' />
        ))}
        {hasActionsColumn && <th className='indent-cell' />}
      </tr>

      {subRows.map((subRow, subIndex) => {
        const subRowKey = `${rowKey}.${subIndex}`;
        const subRowRec = subRow as Record<string, unknown>;
        const editingSubRow = editingSubRows?.get(subRowKey) ?? null;
        const subRowErrors = subRowEditErrors?.get(subRowKey);

        return (
          <tr
            key={`${rowKey}-sub-${subIndex}`}
            className='child-row'
            data-testid={`dt-v2-sub-row-${rowIndex}-${subIndex}`}>
            {indentEnabled && <td className='indent-cell' />}
            {subRowColumns.map((subCol) => {
              const isSubRowEditing = editingSubRow !== null && subCol.editable != null;

              return (
                <td
                  key={subCol.id}
                  style={{
                    textAlign:
                      subCol.align === 'center'
                        ? 'center'
                        : subCol.align === 'right'
                        ? 'right'
                        : 'left',
                    width: subCol.width.default,
                  }}
                  onDoubleClick={
                    subCol.editable
                      ? () => onSubRowCellDoubleClick?.(rowKey, subIndex, subCol.field)
                      : undefined
                  }
                  data-testid={`dt-v2-sub-cell-${rowIndex}-${subIndex}-${subCol.field}`}>
                  <CellRenderer
                    column={subCol}
                    row={subRowRec}
                    rowIndex={subIndex}
                    isEditing={isSubRowEditing}
                    registry={registry}
                    injectedData={subRowInjectedData}
                    onDoubleClick={
                      subCol.editable
                        ? () => onSubRowCellDoubleClick?.(rowKey, subIndex, subCol.field)
                        : undefined
                    }
                    editValue={editingSubRow?.[subCol.field]}
                    onEditChange={(val) =>
                      onSubRowEditChange?.(rowKey, subIndex, subCol.field, val)
                    }
                    onEditBlur={() => onSubRowEditBlur?.(rowKey, subIndex, subCol.field)}
                    editError={subRowErrors?.[subCol.field]}
                    editorAutoFocus={editorAutoFocus}
                  />
                </td>
              );
            })}
            {Array.from({ length: fillerCount }, (_, i) => (
              <td key={`filler-d-${i}`} className='indent-cell' />
            ))}
            {hasActionsColumn && (
              <td style={{ overflow: 'hidden' }}>
                {hasSubRowActions && (
                  <ActionRenderer
                    actions={subRowActions!}
                    row={subRowRec}
                    rowIndex={subIndex}
                    namespace={namespace}
                    onExecuteAction={onExecuteAction}
                    checkPermission={checkPermission}
                    subRowContext={{
                      parentRow: row,
                      parentRowKey: rowKey,
                      subIndex,
                      isSubRow: true,
                    }}
                  />
                )}
              </td>
            )}
          </tr>
        );
      })}
    </>
  );
};
